import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import tw, { styled } from 'twin.macro'

const Card = styled.div`
	${tw`bg-white transition-transform duration-300 hover:scale-105 flex flex-col`}
	border-radius: 40px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08);
	width: 100%;
	max-width: 400px;
`

const CardImageWrapper = styled.div`
	${tw`relative w-full bg-white p-4 flex-shrink-0`}
	height: 450px;
	display: block;
	border-radius: 40px 40px 0 0;
`

const ImagePlaceholder = styled.div`
	${tw`w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200`}
	border-radius: 40px;
`

const PlaceholderText = styled.span`
	${tw`text-gray-400 font-medium`}
`

const CardImage = styled.img`
	${tw`w-full h-full object-fill`}
	border-radius: 24px;
`

const CardContent = styled.div`
	${tw`p-6 flex flex-col flex-grow`}
	border-radius: 0 0 40px 40px;
`

const CardTitle = styled.h3`
	${tw`text-lg font-semibold text-gray-900`}
	margin-bottom: 16px;
`

const CardDescription = styled.div`
	${tw`text-sm text-gray-600 mb-4 flex-grow`}
	font-weight: 400;
	
	.expanded-content {
		${tw`mb-4`}
		line-height: 1.6;
		
		p {
			${tw`mb-2 last:mb-0`}
		}
	}
`

const ReadMoreButton = styled.button`
	${tw`text-black font-bold hover:text-gray-700 transition-colors duration-200`}
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	margin-top: 12px;
`

const CardButton = styled(Link)`
	${tw`inline-block px-6 py-3 rounded-lg font-bold text-center uppercase transition-colors duration-300 mb-4`}
	${({ variant, $hasImage }) => {
		if ($hasImage) {
			return tw`bg-transparent p-0 hover:bg-transparent`
		}
		switch (variant) {
			case 'primary':
				return tw`bg-yellow-500 text-white hover:bg-yellow-600`
			case 'secondary':
				return tw`bg-black text-white hover:bg-gray-800`
			case 'tertiary':
				return tw`bg-purple-600 text-white hover:bg-purple-700`
			default:
				return tw`bg-blue-500 text-white hover:bg-blue-600`
		}
	}}
`

const ButtonImage = styled.img`
	${tw`w-full h-auto`}
	display: block;
	transition: opacity 0.3s ease;
`

const ButtonImageWrapper = styled.div`
	${tw`relative`}
	width: 100%;

	.button-image-default {
		${tw`block w-full`}
		opacity: 1;
	}

	.button-image-hover {
		${tw`absolute top-0 left-0 w-full`}
		opacity: 0;
		pointer-events: none;
	}

	&.has-hover-image:hover {
		.button-image-default {
			opacity: 0;
		}

		.button-image-hover {
			opacity: 1;
		}
	}
`

const BadgeContainer = styled.div`
	${tw`absolute top-4 right-4 flex flex-col gap-2`}
`

const Badge = styled.span`
	${tw`px-3 py-1 rounded-full text-sm font-bold text-white shadow-lg`}
	${({ color }) => {
		switch (color) {
			case 'purple':
				return tw`bg-purple-600`
			case 'orange':
				return tw`bg-yellow-500`
			case 'green':
				return tw`bg-green-500`
			default:
				return tw`bg-blue-500`
		}
	}}
`

/**
 * PromotionCard - Reusable card component for promotions
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {string} props.imageSrc - Image source (optional, shows placeholder if not provided)
 * @param {string} props.imageAlt - Image alt text
 * @param {string} props.buttonText - Button label (used if no button images provided)
 * @param {string} props.buttonImageSrc - Button image source (optional)
 * @param {string} props.buttonImageHoverSrc - Button hover image source (optional)
 * @param {string} props.buttonImageAlt - Button image alt text
 * @param {string} props.buttonLink - Button link destination
 * @param {string} props.buttonVariant - Button style variant (primary, secondary, tertiary)
 * @param {Array} props.badges - Array of badge objects {text: string, color: string}
 * Badge colors: purple, orange (displays as yellow), green, default
 */
const PromotionCard = ({
	title,
	description,
	imageSrc,
	imageAlt,
	buttonText,
	buttonImageSrc,
	buttonImageHoverSrc,
	buttonImageAlt,
	buttonLink,
	buttonVariant,
	badges,
}) => {
	const [isExpanded, setIsExpanded] = useState(false)

	return (
		<Card>
			<CardImageWrapper>
				{badges && badges.length > 0 && (
					<BadgeContainer>
						{badges.map((badge, index) => (
							<Badge key={index} color={badge.color}>
								{badge.text}
							</Badge>
						))}
					</BadgeContainer>
				)}
				{imageSrc ? (
					<CardImage src={imageSrc} alt={imageAlt} />
				) : (
					<ImagePlaceholder>
						<PlaceholderText>Promotion Image (525x525)</PlaceholderText>
					</ImagePlaceholder>
				)}
			</CardImageWrapper>
			<CardContent>
				<CardButton to={buttonLink} variant={buttonVariant} $hasImage={!!buttonImageSrc}>
					{buttonImageSrc ? (
						<ButtonImageWrapper className={buttonImageHoverSrc ? 'has-hover-image' : ''}>
							<ButtonImage
								src={buttonImageSrc}
								alt={buttonImageAlt || buttonText}
								className="button-image-default"
							/>
							{buttonImageHoverSrc && (
								<ButtonImage
									src={buttonImageHoverSrc}
									alt={buttonImageAlt || buttonText}
									className="button-image-hover"
								/>
							)}
						</ButtonImageWrapper>
					) : (
						buttonText
					)}
				</CardButton>
				<CardTitle>{title}</CardTitle>
				<CardDescription>
					{isExpanded && (
						<div className="expanded-content">
							{description.split('\n').map((line, index) => (
								<p key={index} css={tw`mb-2`}>
									{line.trim()}
								</p>
							))}
						</div>
					)}
					<ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>
						{isExpanded ? 'Read Less' : 'Read More'}
					</ReadMoreButton>
				</CardDescription>
			</CardContent>
		</Card>
	)
}

PromotionCard.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	imageSrc: PropTypes.string,
	imageAlt: PropTypes.string,
	buttonText: PropTypes.string,
	buttonImageSrc: PropTypes.string,
	buttonImageHoverSrc: PropTypes.string,
	buttonImageAlt: PropTypes.string,
	buttonLink: PropTypes.string.isRequired,
	buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'default']),
	badges: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			color: PropTypes.oneOf(['purple', 'orange', 'green', 'default']),
		})
	),
}

PromotionCard.defaultProps = {
	imageSrc: null,
	imageAlt: 'Promotion image',
	buttonText: '',
	buttonImageSrc: null,
	buttonImageHoverSrc: null,
	buttonImageAlt: '',
	buttonVariant: 'default',
	badges: [],
}

export default PromotionCard
